import React, { useState, useEffect, useRef } from 'react';
import { API, getToken } from './adminApi';

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—';

// The tests results can be shown for (testName in ABTestExposure).
// 83000 ms → "1m 23s".
const fmtMs = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

const fmtSecs = (s) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

// "Watch the video" test: did this variant's visitors watch it, and for how long.
const videoCell = (w, exposures) => {
  if (!w || !w.opened) return <span className="as-ab-hint">Not watched</span>;
  const pct = exposures ? Math.round((w.opened / exposures) * 100) : 0;
  return (
    <>
      <div>{w.opened} opened ({pct}%) · {w.played} played · {w.half} watched half · {w.finished} finished</div>
      <div className="as-ab-hint">
        {w.timed
          ? `Watched for: average ${fmtSecs(w.avgSeconds)} · median ${fmtSecs(w.medianSeconds)} · longest ${fmtSecs(w.longestSeconds)} (${w.timed} timed)`
          : 'Watch time recorded from 1 Nov — none yet'}
      </div>
    </>
  );
};

// Every variant a test has, in order — one with no visitors yet still gets
// its row (e.g. the video test's C, added later).
const variantRows = (testName, variants) => {
  const keys = Object.keys(TESTS[testName]?.labels || {});
  const extra = variants.filter(v => !keys.includes(v.variant));
  return [
    ...keys.map(k => variants.find(v => v.variant === k) || { variant: k, exposures: 0, conversions: 0, conversionRate: 0 }),
    ...extra,
  ];
};

const TESTS = {
  community_home_demo_button: { title: 'Community Home "Demo" Button', labels: { A: 'A — Demo button shown', B: 'B — Demo button hidden' } },
  signup_page_blurb: { title: 'Sign-up page — group description', labels: { A: 'A — description shown', B: 'B — no description, bigger "Free…" line' } },
  // Counts people who tapped Join, by where it took them.
  community_join_route: { title: 'Join — sign-up form or chat (people who tapped Join)', labels: { A: 'A — sign-up form', B: 'B — chat sign-up (/meet)' } },
  community_about_video: { title: 'Community page — "Watch the video"', labels: { A: 'A — no video', B: 'B — video button over the hero', C: 'C — video in a hexagon under the title' } },
  community_hero_flow: { title: 'Community Home hero', labels: { A: 'A — normal hero', B: 'B — quick-match', C: 'C — quick-match, join-or-look choice' } },
};

const ABTestTab = () => {
  const [testName, setTestName] = useState('community_home_demo_button');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const [demoSessions, setDemoSessions] = useState(null);
  const [demoSessionsLoading, setDemoSessionsLoading] = useState(false);
  const [openVariant, setOpenVariant] = useState(null); // 'A' | 'B' | null
  const [openSessionId, setOpenSessionId] = useState(null);

  useEffect(() => { fetchResults(); }, [testName]); // eslint-disable-line react-hooks/exhaustive-deps

  // The test whose results should show — a slower answer for a test you've
  // since moved away from is ignored.
  const shownTest = useRef(testName);
  shownTest.current = testName;

  const fetchResults = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/ab-test-results?testName=${encodeURIComponent(testName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      if (json.testName === shownTest.current) setData(json);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDemoSessions = async () => {
    setDemoSessionsLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/admin/ab-test-demo-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setDemoSessions(json.sessions);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setDemoSessionsLoading(false);
    }
  };

  const toggleVariant = (variant) => {
    if (openVariant === variant) {
      setOpenVariant(null);
      return;
    }
    setOpenVariant(variant);
    setOpenSessionId(null);
    if (!demoSessions) fetchDemoSessions();
  };

  const sessionsForVariant = (variant) => (demoSessions || []).filter(s => s.variant === variant);

  return (
    <div className="as-signups">
      {fetchError && <div className="as-error">{fetchError}</div>}
      <div className="as-activity-toolbar">
        <select className="as-pa-select" value={testName} onChange={e => { setData(null); setOpenVariant(null); setTestName(e.target.value); }} aria-label="Test">
          {Object.entries(TESTS).map(([k, t]) => <option key={k} value={k}>{t.title}</option>)}
        </select>
        <button className="as-filter-tab" onClick={() => { fetchResults(); setDemoSessions(null); setOpenVariant(null); }}>↻ Refresh</button>
      </div>

      {loading && <div className="as-loading">Loading…</div>}

      {data && (
        <>
          <h2 className="as-section-title">🧪 A/B Test — {TESTS[testName].title}</h2>

          {/* Which communities a per-community test is running on. */}
          {data.video && (
            <p className="as-ab-hint">
              Runs on: {data.video.runningOn.length ? data.video.runningOn.join(', ') : 'no community right now'} — communities with a video and its A/B test ticked
              (Manage Communities). Every visitor sees the hero as normal; A gets no video, B a button over the hero, C the video in its own section below it.
            </p>
          )}

          {data.variants.length === 0 && <div className="as-empty">No exposures logged yet.</div>}

          {data.variants.length > 0 && (
            <div className="as-act-table-wrap">
              <table className="as-ab-table">
                <thead>
                  <tr>
                    <th>Variant</th>
                    <th>Exposures</th>
                    <th>Signups</th>
                    <th>Conversion Rate</th>
                    <th>First Seen</th>
                    <th>Last Seen</th>
                    {data.video && <th>Video watched</th>}
                  </tr>
                </thead>
                <tbody>
                  {variantRows(testName, data.variants).map(v => (
                    <tr key={v.variant}>
                      <td>{TESTS[testName].labels[v.variant] || v.variant}</td>
                      <td>{v.exposures}</td>
                      <td>{v.conversions}</td>
                      <td>{v.conversionRate}%</td>
                      <td>{fmtDate(v.firstExposure)}</td>
                      <td>{fmtDate(v.lastExposure)}</td>
                      {data.video && <td>{videoCell(data.video.byVariant[v.variant], v.exposures)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* How each variant's visitors behaved — Page Analytics' measures. */}
          {/* Not for the Join test: the page is the same for both routes until Join is tapped. */}
          {testName !== 'community_join_route' && data.behaviour && Object.keys(data.behaviour).length > 0 && (
            <>
              <h3 className="as-section-title" style={{ marginTop: 24 }}>On the page</h3>
              <p className="as-ab-hint">
                Their visits to the group's page from when they were put in the test (admin and internal browsers left out).
                Time on page leaves out visits over 3 minutes; bounce = left within 10s without scrolling a quarter or clicking;
                returning = came back in a later sitting (30+ minutes on).
              </p>
              <div className="as-act-table-wrap">
                <table className="as-ab-table">
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th>Visits</th>
                      <th>Visitors</th>
                      <th>Time on page</th>
                      <th>Bounce</th>
                      <th>Avg scroll</th>
                      <th>Returning</th>
                      <th>Clicked Join</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantRows(testName, data.variants).map(({ variant }) => {
                      const b = data.behaviour[variant];
                      return (
                        <tr key={variant}>
                          <td>{TESTS[testName].labels[variant] || variant}</td>
                          {b ? (
                            <>
                              <td>{b.visits}</td>
                              <td>{b.visitors}</td>
                              <td>{fmtMs(b.avgTimeMs)} <span className="as-ab-hint">median {fmtMs(b.medianTimeMs)}</span></td>
                              <td>{b.bounceRate}%</td>
                              <td>{b.avgScroll}%</td>
                              <td>{b.returningVisitors} <span className="as-ab-hint">({b.returningRate}%)</span></td>
                              <td>{b.joinClicks} <span className="as-ab-hint">({b.joinClickRate}%)</span></td>
                            </>
                          ) : <td colSpan={7} className="as-ab-hint">No visits yet</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Video test: did pressing play go with tapping Join / Demo and signing up? */}
          {data.videoFunnel && Object.keys(data.videoFunnel).length > 0 && (() => {
            const pct = (n, of) => (of ? ` (${Math.round((n / of) * 100)}%)` : '');
            const rows = [
              ...variantRows(testName, data.variants).map(({ variant }) => [variant, TESTS[testName].labels[variant] || variant]),
              ['all', 'All variants together'],
            ].filter(([k]) => data.videoFunnel[k]);
            return (
              <>
                <h3 className="as-section-title" style={{ marginTop: 24 }}>Played the video → Join / Demo → signed up</h3>
                <p className="as-ab-hint">
                  Each variant's visitors split by whether they pressed play, then how many of each tapped Join or Demo, and how many signed up
                  (admin and test visitors left out). A has no video, so everyone there is "didn't play". With few visitors, treat differences as hints, not proof.
                </p>
                <div className="as-act-table-wrap">
                  <table className="as-ab-table">
                    <thead>
                      <tr><th>Variant</th><th>Visitors</th><th>Tapped Join</th><th>Tapped Demo</th><th>Join or Demo</th><th>Signed up</th></tr>
                    </thead>
                    <tbody>
                      {rows.flatMap(([k, label]) => [['played', 'played the video'], ['notPlayed', "didn't play it"]]
                        .filter(([g]) => !(k === 'A' && g === 'played'))
                        .map(([g, gLabel]) => {
                          const f = data.videoFunnel[k][g];
                          return (
                            <tr key={`${k}-${g}`}>
                              <td>{label} <span className="as-ab-hint">— {gLabel}</span></td>
                              <td>{f.visitors}</td>
                              <td>{f.clickedJoin}<span className="as-ab-hint">{pct(f.clickedJoin, f.visitors)}</span></td>
                              <td>{f.clickedDemo}<span className="as-ab-hint">{pct(f.clickedDemo, f.visitors)}</span></td>
                              <td>{f.clickedEither}<span className="as-ab-hint">{pct(f.clickedEither, f.visitors)}</span></td>
                              <td>{f.signedUp}<span className="as-ab-hint">{pct(f.signedUp, f.visitors)}</span></td>
                            </tr>
                          );
                        }))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}

          {testName === 'community_home_demo_button' && (
          <>
          <h3 className="as-section-title" style={{ marginTop: 24 }}>Demo Bot Engagement</h3>
          <p className="as-ab-hint">
            How deep visitors actually went in the "Try Now" chat — not just whether they eventually signed up.
            Variant B naturally has few or none, since there's no button to click into it.
          </p>
          <div className="as-ab-demo-toggles">
            {(data.variants.length > 0 ? data.variants : [{ variant: 'A' }, { variant: 'B' }]).map(v => {
              const count = demoSessions ? sessionsForVariant(v.variant).length : null;
              return (
                <button
                  key={v.variant}
                  className={`as-filter-tab ${openVariant === v.variant ? 'as-filter-tab--active' : ''}`}
                  onClick={() => toggleVariant(v.variant)}
                >
                  🤖 {v.variant} — AI Try Now Sessions {count != null ? `(${count})` : ''} {openVariant === v.variant ? '▲' : '▼'}
                </button>
              );
            })}
          </div>

          {demoSessionsLoading && <div className="as-loading">Loading sessions…</div>}

          {openVariant && demoSessions && (
            <div className="as-ab-session-list">
              {sessionsForVariant(openVariant).length === 0 && (
                <div className="as-empty">No demo sessions for variant {openVariant} yet.</div>
              )}
              {sessionsForVariant(openVariant).map(s => (
                <div key={s.sessionId} className="as-ab-session-card">
                  <button
                    className="as-ab-session-header"
                    onClick={() => setOpenSessionId(openSessionId === s.sessionId ? null : s.sessionId)}
                  >
                    <span className="as-ab-session-community">{s.communityTitle}</span>
                    <span>{s.userMessageCount} message{s.userMessageCount === 1 ? '' : 's'} sent</span>
                    <span>{s.reachedProfileSuggestions ? '✅ Reached suggestions' : '— Ended early'}</span>
                    <span>{s.reachedSignup ? '🎉 Clicked signup' : ''}</span>
                    <span className="as-ab-session-date">{fmtDate(s.startedAt)}</span>
                  </button>
                  {openSessionId === s.sessionId && (
                    <div className="as-ab-session-transcript">
                      {s.messages.length === 0 && <div className="as-empty">No messages recorded.</div>}
                      {s.messages.map((m, i) => (
                        <div key={i} className={`as-ab-transcript-line as-ab-transcript-${m.role}`}>
                          <strong>{m.role === 'user' ? 'Visitor' : 'AI'}:</strong> {m.content}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          </>
          )}

          {data.byCommunity.length > 0 && (
            <>
              <h3 className="as-section-title" style={{ marginTop: 24 }}>By Community</h3>
              <table className="as-ab-table">
                <thead>
                  <tr>
                    <th>Community</th>
                    <th>Variant</th>
                    <th>Exposures</th>
                    <th>Signups</th>
                    <th>Conversion Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCommunity.map((r, i) => (
                    <tr key={`${r.communityId}-${r.variant}-${i}`}>
                      <td>{r.communityTitle}</td>
                      <td>{r.variant}</td>
                      <td>{r.exposures}</td>
                      <td>{r.conversions}</td>
                      <td>{r.conversionRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ABTestTab;
